import { UpdateResult } from '@dittolive/ditto'
import {
  useMutations,
  usePendingIDSpecificOperation,
} from '@dittolive/react-ditto'
import { SystemCollections, SystemIds } from 'components/dittoAppConnection'
import { useContext } from 'react'

import {
  buildNewPlaygroundAuthObject,
  PLAYGROUND_AUTH_PROVIDER,
} from '../../dittoPortalProvider/defaultAuthSettings'
import AppSettingsContext from '../../scenes/appManagement/context'

// Types of token webhooks. Today we support token liveQueryWebhooks and anonymous.  Anonymous is only used for playground mode
type WebhookType = 'tokenWebhook'

export type AuthWebhookConfiguration = {
  type: WebhookType
  webhookUrl: string
  version?: string
}

type AnonymousType = 'anonymous'

export type AnonymousProviderConfiguration = {
  type: AnonymousType
  sharedToken: string
  sessionLength: number
  permission: Record<string, unknown>
}

export type AuthenticationConfig = {
  providers: {
    [webhookName: string]:
      | AuthWebhookConfiguration
      | AnonymousProviderConfiguration
  }
}

export function getWebhookConfig(
  config: AuthenticationConfig | null | undefined,
  providerName: string,
): AuthWebhookConfiguration | undefined {
  switch (config?.providers[providerName]?.type) {
    case 'tokenWebhook':
      return config.providers[providerName] as AuthWebhookConfiguration
    default:
      return undefined
  }
}

export function getPlaygroundProvider(
  config: AuthenticationConfig | null | undefined,
): AnonymousProviderConfiguration | undefined {
  switch (config?.providers[PLAYGROUND_AUTH_PROVIDER]?.type) {
    case 'anonymous':
      return config.providers[
        PLAYGROUND_AUTH_PROVIDER
      ] as AnonymousProviderConfiguration
    default:
      return undefined
  }
}

export type AppAuthenticationReturn = {
  configuration?: AuthenticationConfig | null
  isAuthEnabled?: boolean
  togglePlaygroundMode: () => Promise<UpdateResult[]>
  refreshPlaygroundToken: () => Promise<UpdateResult[]>
  createWebhook: (
    name: string,
    url: string,
    legacy: boolean,
  ) => Promise<UpdateResult[]>
  updateWebhook: (
    fromName: string,
    name: string,
    url: string,
  ) => Promise<UpdateResult[]>
  deleteWebhook: (name: string) => Promise<UpdateResult[]>
}

type Params = {
  appId?: string
}

/** React hook for managing authentication webhooks */
const useAppAuthentication = (params: Params = {}): AppAuthenticationReturn => {
  const ctx = useContext(AppSettingsContext)

  const appId = params.appId || ctx.appId

  const { document: configuration } = usePendingIDSpecificOperation({
    path: appId,
    _id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
    collection: SystemCollections.APP_WEBHOOKS_COLLECTION,
  })

  const { updateByID } = useMutations({
    path: appId,
    collection: SystemCollections.APP_WEBHOOKS_COLLECTION,
  })

  // This looks to see if an application is in production or playground mode.  This logic can seem a bit confusing
  // because there is literally a flag on the config called "authenticationEnabled" but that is referencing something
  // else.  The config field decides if the big peer is requiring a valid session for access, not if it is playground
  // mode or not
  const isAuthEnabled = !(
    configuration?.value.providers?.hasOwnProperty(PLAYGROUND_AUTH_PROVIDER) ||
    (configuration?.value.authenticationEnabled != undefined &&
      configuration?.value.authenticationEnabled == false)
  )

  const togglePlaygroundMode = async () =>
    await updateByID({
      _id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
      updateClosure: (currentConfig) => {
        // Our goal is really to remove this field but will still need it during the period of migration.  When an
        // application has auth enabled we need to signal to hydra that a valid session is required.  That is what
        // this flag has been doing, and we need it for just a little longer.
        //
        // Previously we left authenticationEnabled as false for the new OnlinePlayground mode configuration as an
        // overly cautious way to ease into the change.  This new identity has been out for some time, the SDKs
        // are readily available and had a fair amount of testing.  Now next time any application is updated it will be
        // forced into requiring a session, either OnlineWithAuthentication or OnlinePlayground mode.
        currentConfig.at('authenticationEnabled').set(true)
        if (isAuthEnabled) {
          currentConfig
            .at(`providers.${PLAYGROUND_AUTH_PROVIDER}`)
            .set(buildNewPlaygroundAuthObject())
        } else {
          currentConfig.at(`providers.${PLAYGROUND_AUTH_PROVIDER}`).remove()
        }
      },
    })

  const refreshPlaygroundToken = async () =>
    await updateByID({
      _id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
      updateClosure: (currentConfig) => {
        currentConfig
          .at(`providers.${PLAYGROUND_AUTH_PROVIDER}`)
          .set(buildNewPlaygroundAuthObject())
      },
    })

  const createWebhook = async (name: string, url: string, legacy: boolean) =>
    await updateByID({
      _id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
      updateClosure: (document) => {
        document.at(`providers["${name}"]`).set({
          type: 'tokenWebhook',
          webhookUrl: url,
          ...(legacy ? {} : { version: '1' }),
        })
      },
    })

  const updateWebhook = async (fromName: string, toName: string, url: string) =>
    await updateByID({
      _id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
      updateClosure: (document) => {
        if (fromName !== toName) {
          document.at(`providers["${fromName}"]`).remove()
        }

        document.at(`providers["${toName}"]`).set({
          type: 'tokenWebhook',
          webhookUrl: url,
        })
      },
    })

  const deleteWebhook = async (providerName: string) =>
    await updateByID({
      _id: SystemIds.AUTH_WEBHOOKS_CONFIGURATION,
      updateClosure: (document) => {
        document.at(`providers["${providerName}"]`).remove()
      },
    })

  return {
    configuration: configuration?.value as AuthenticationConfig,
    isAuthEnabled,
    togglePlaygroundMode,
    refreshPlaygroundToken,
    updateWebhook,
    deleteWebhook,
    createWebhook,
  }
}

export default useAppAuthentication
